
// Libaries
import { Component, Vue, namespace } from "nuxt-property-decorator";
import { Descriptions, Products } from "~/operations/messages";

// Components
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import DataTile from "~/components/organisms/display/DataTile.vue";
import PriceComparisonHistoricalData from "~/components/organisms/display/PriceComparisonHistoricalData.vue";
import HeatingOilDataDisplay from "~/components/organisms/display/geographySites/HeatingOilDataDisplay.vue";

import { getHistoricalData } from "~/utils/calculateStatistics";

import {
  getCurrentDate,
  getFormattedPercentage,
  getPriceDifference,
  replaceToUmlauts,
  returnPriceFormatDE,
} from "~/mixins/formatting";
import { decodePlaceName } from "~/operations/shared";

// Mixins
import { Jsonld } from "nuxt-jsonld";
import striptags from "striptags";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import DistrictFormSection from "~/components/molecules/display/DistrictFormSection.vue";
import DistrictOtherLocations from "~/components/molecules/display/DistrictOtherLocations.vue";
import DistrictPriceInfo from "~/components/molecules/display/DistrictPriceInfo.vue";
import { createBreadCrumbsStructuredData } from "~/mixins/breadcrumb";

import DistrictLocationsList from "~/components/molecules/display/DistrictLocationsList.vue";
import DistrictUnifiedPriceChart from "~/components/organisms/chart/DistrictUnifiedPriceChart.vue";
import {
  getFormattedTitle,
  getPriceComparisonText7Days,
  getPriceComparisonTextThreeMonths,
  getPriceDifferenceText,
} from "~/mixins/oilPricemixin";
import {
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/translations";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { HistoricalOilPriceData } from "~/types/HistoricalOilPriceData";

const FederalStatesModule = namespace("federalstates");
const BreadCrumbModule = namespace("breadcrumb");

@Jsonld
@Component({
  scrollToTop: true,
  components: {
    RouterLink,
    DataTile,
    BreadCrumb,
    PriceComparisonHistoricalData,
    DistrictLocationsList,
    DistrictOtherLocations,
    DistrictUnifiedPriceChart,
    DistrictPriceInfo,
    DistrictFormSection,
    HeatingOilDataDisplay,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
    getCurrentDate,
  },
})
export default class CountyPricesView extends Vue {
  head() {
    return {
      ...generateMetaTags(
        this.$route.path,
        this.isDistrictCity ? "district-city" : "landkreis",
        [{ k: "name", v: this.countyName }],
      ),
      link: [
        {
          rel: "canonical",
          href: `/heizoelpreise/bundeslaender/${this.$route.params.category.toLowerCase()}/${this.routeCountyName.toLowerCase()}`,
        },
      ],
      script: [
        {
          json: {
            "@context": "http://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: `Was kostet Heizöl ${this.locationTypePrefix} ${this.countyName}?`,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: striptags(this.questionListWithAnswers[0].answer),
                },
              },
            ],
          },
          type: "application/ld+json",
        },
        {
          json: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(this.breadCrumb),
          },
          type: "application/ld+json",
        },
      ],
    };
  }

  Products = Products;
  Descriptions = Descriptions;
  questionList = [];
  translateStateName = translateStateName;

  @BreadCrumbModule.Mutation("setBreadCrumbForBundeslandAndCounty")
  setBreadCrumbForBundeslandAndCounty;

  @BreadCrumbModule.State("breadCrumb")
  breadCrumb;

  @FederalStatesModule.State("countyLinks")
  countyLinks;

  @FederalStatesModule.State("communityLinks")
  communityLinks;

  @FederalStatesModule.State("stateYearStatistics")
  stateYearStatistics;

  @FederalStatesModule.State("districtCities")
  districtCities;

  @FederalStatesModule.State("districtCityZipcodes")
  districtCityZipcodes;

  @FederalStatesModule.State("countyYearStatistics")
  countyYearStatistics;

  @FederalStatesModule.State("statesOilPriceHistory")
  statesOilPriceHistory;

  @FederalStatesModule.State("stateAssetsPrice")
  stateAssetsPrice;

  @FederalStatesModule.State("countyNutsCode")
  countyNutsCode;

  @FederalStatesModule.State("countyName")
  countyName;

  monthsQuantity = 6;

  get questionListWithAnswers() {
    const currentPriceValue = this.stateAssetsPrice.currentPrice.value;
    const lastPriceValue = this.stateAssetsPrice.lastPrice.value;
    const formattedCurrentPrice = returnPriceFormatDE(
      this.stateAssetsPrice.currentPrice.value,
    );

    const currentDate = getCurrentDate();
    const { difference, numericCurrentPrice, numericLastPrice } =
      getPriceDifference(currentPriceValue, lastPriceValue);

    const { priceStatus, priceVariation } = this.getPriceStatusAndVariation(
      numericCurrentPrice,
      numericLastPrice,
      currentPriceValue,
      lastPriceValue,
    );

    this.questionList = [
      {
        question: `Was kostet Heizöl ${this.locationTypePrefix} ${this.countyName}?`,
        answer: this.generateAnswerText(
          currentDate,
          formattedCurrentPrice,
          difference,
          priceStatus,
          priceVariation,
        ),
        visibility: true,
      },
    ];
    return this.questionList;
  }

  private getPriceStatusAndVariation(
    numericCurrentPrice: number,
    numericLastPrice: number,
    currentPriceValue: number,
    lastPriceValue: number,
  ) {
    return numericCurrentPrice > numericLastPrice
      ? {
          priceStatus: "mehr",
          priceVariation: `einem Preisanstieg von ${getFormattedPercentage(
            currentPriceValue,
            lastPriceValue,
          )}`,
        }
      : {
          priceStatus: "weniger",
          priceVariation: `einer Preissenkung von ${getFormattedPercentage(
            currentPriceValue,
            lastPriceValue,
          )}`,
        };
  }

  private generateAnswerText(
    currentDate: string,
    formattedCurrentPrice: string,
    difference: number,
    priceStatus: string,
    priceVariation: string,
  ): string {
    return `<div>
      <p style='padding: 10px 0;'>
        Der Liter Heizöl kostet am ${currentDate} ${this.locationTypePrefix} ${
      this.countyName
    } im Durchschnitt ${formattedCurrentPrice.replace("€", "")}Cent.
        Das sind ${String(difference / 100).replace(
          ".",
          ",",
        )} Cent ${priceStatus} als am Vortag.
      </p>
      <p style='padding: 10px 0;'>
        Das entspricht ${priceVariation}.
      </p>
    </div>`;
  }

  get locationTypePrefix(): string {
    return this.isDistrictCity ? "in kreisfreier Stadt" : "im Landkreis";
  }

  async updateBreadcrumbs(): Promise<void> {
    const { formatBreadCrumbByCountyAndBundesland } = await import(
      "~/mixins/breadcrumb"
    );

    const crumbs = formatBreadCrumbByCountyAndBundesland(
      this.$route.fullPath,
      this.$router,
      this.countyName,
    );

    this.setBreadCrumbForBundeslandAndCounty({
      breadCrumbData: crumbs,
      fullPath: this.$route.fullPath,
    });
  }

  async fetch() {
    try {
      await this.fetchInitialData();
      await Promise.all([this.fetchLocationData(), this.fetchPriceData()]);
      this.updateBreadcrumbs();
    } catch (error) {
      this.$nuxt.error({
        statusCode: 404,
        message: "Diese Ortschaft konnte nicht gefunden werden",
      });
    }
  }

  async mounted() {
    await this.fetchCountyIdentData(
      translateStateName(this.$route.params.category),
      translateStateName(this.routeCountyName),
    );
    const stateCity = translateStateName(
      replaceToUmlauts(this.$route.params.category),
    );
    if (["Bremen", "Berlin", "Hamburg"].includes(stateCity)) {
      await this.fetchChartAssetsState();
    } else {
      await this.fetchChartAssets();
    }
  }

  beforeDestroy() {
    /* resolves the bug when you click on the landkreis and go back to bundeslandsite and click the same landkreis again
     the cities will not be shown but instead the landkreise
     One possible solution is to set the countynutscode to null when the component unmounts
     */
    this.$store.commit("federalstates/resetCountyInfo");
  }

  async fetchInitialData(): Promise<void> {
    try {
      this.redirectToWithoutUmlauts();
      await this.fetchCountyIdentData(
        translateStateName(this.$route.params.category),
        translateStateName(this.routeCountyName),
      );
    } catch (error) {
      this.$nuxt.error({
        statusCode: 404,
        message: "Diese Ortschaft konnte nicht gefunden werden",
      });
    }
  }

  async fetchLocationData(): Promise<void> {
    if (this.isDistrictCity) {
      await Promise.all([
        this.fetchOtherDistrictCities(),
        this.fetchDistrictCityZipcodes(
          translateStateName(this.routeCountyName),
        ),
      ]);
    } else {
      const stateNutsCode = translateStateToNutsCode(
        replaceToUmlauts(this.$route.params.category),
      );
      await Promise.all([
        this.fetchCounties(stateNutsCode),
        this.fetchCommunities(this.countyNutsCode),
      ]);
    }
  }

  async fetchPriceData(): Promise<void> {
    const stateName = translateStateName(
      replaceToUmlauts(this.$route.params.category),
    );
    const stateNutsCode = translateStateToNutsCode(
      replaceToUmlauts(this.$route.params.category),
    );

    const isDistrictCityState = ["Bremen", "Berlin", "Hamburg"].includes(
      stateName,
    );

    if (isDistrictCityState) {
      await this.fetchCurrentStateOilPrice(stateNutsCode);
      await this.fetchChartYearAssets();
    } else {
      await Promise.all([
        this.fetchStateCountyData(stateNutsCode, this.countyNutsCode),
        this.fetchCountyYearData(this.countyNutsCode),
      ]);
    }
  }

  async fetchCountyIdentData(
    stateName: string,
    countyName: string,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyIdentData", {
      stateName,
      countyName,
    });
  }

  async fetchOtherDistrictCities(): Promise<void> {
    const stateNutsCode = translateStateToNutsCode(
      replaceToUmlauts(this.$route.params.category),
    );

    await this.$store.dispatch("federalstates/fetchDistrictCities", {
      stateNutsCode,
      shouldFilter: true,
      includeLinkField: true,
      currentDistrictCity: replaceToUmlauts(this.countyName),
    });
  }

  async fetchChartAssetsState(): Promise<void> {
    this.$store.dispatch("federalstates/loadNationAndStateData", {
      nutsCode: translateStateToNutsCode(
        replaceToUmlauts(this.$route.params.category),
      ),
      months: this.monthsQuantity,
    });
  }

  async fetchDistrictCityZipcodes(districtCity: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchDistrictCityZipcodes", {
      districtCity,
      urlPrefix: "heizoelpreise",
    });
  }

  async fetchCommunities(statesNutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCommunityLinks", {
      communityNutsCode: statesNutsCode,
    });
  }

  async fetchCounties(statesNutsCode: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyLinks", {
      statesNutsCode,
    });
  }

  async fetchChartYearAssets(): Promise<void> {
    await this.$store.dispatch("federalstates/fetchStateYearData", {
      nutsCode: translateStateToNutsCode(
        replaceToUmlauts(this.$route.params.category),
      ),
    });
  }

  async fetchStateCountyData(
    stateNuts: string,
    countyNuts: string,
  ): Promise<void> {
    await this.$store.dispatch("federalstates/fetchStateCountyData", {
      stateNutsCode: stateNuts,
      countyNutsCode: countyNuts,
    });
  }

  async fetchCountyYearData(countyNuts: string): Promise<void> {
    await this.$store.dispatch("federalstates/fetchCountyYearData", {
      countyNutsCode: countyNuts,
    });
  }

  async fetchCurrentStateOilPrice(statesNutsCode: string) {
    await this.$store.dispatch(
      "federalstates/fetchCurrentStateOilPrice",
      statesNutsCode,
    );
  }

  async fetchChartAssets(): Promise<void> {
    await this.$store.dispatch("federalstates/loadNationStateAndCountyData", {
      stateNutsCode: translateStateToNutsCode(
        replaceToUmlauts(this.$route.params.category),
      ),
      countyNutsCode: this.countyNutsCode,
      months: this.monthsQuantity,
    });
  }

  redirectToWithoutUmlauts(): void {
    const decodedId = decodePlaceName(this.routeCountyName);
    if (this.routeCountyName !== decodedId) {
      const prefix = this.isDistrictCity ? "stadt-" : "";
      this.$nuxt.context.redirect(301, prefix + decodedId);
    }
  }

  changeMonthsQuantity(monthsQuantity: string): void {
    this.monthsQuantity = Number(monthsQuantity);
    this.fetchChartAssets();
  }

  get priceComparisonText(): string {
    return getPriceComparisonTextThreeMonths(
      this.statesOilPriceHistory.countyOiPriceData,
      this.statesOilPriceHistory.labels,
      this.currentAverageCountyPrice,
    );
  }

  get priceComparisonText7Days(): string {
    return getPriceComparisonText7Days(
      this.statesOilPriceHistory.countyOiPriceData,
      this.statesOilPriceHistory.labels,
      this.currentAverageCountyPrice,
    );
  }

  get areaTypeLabel(): string {
    return this.isDistrictCity ? "die kreisfreie Stadt" : "den Landkreis";
  }

  get isDistrictCity(): boolean {
    return this.$route.params.id.startsWith("stadt-");
  }

  get routeCountyName(): string {
    return this.$route.params.id.replace(/^stadt-/, "");
  }

  timeRangeOptions: Array<{ name: string; value: string }> = [
    { name: "3 Monate", value: "3" },
    { name: "6 Monate", value: "6" },
    { name: "1 Jahr", value: "12" },
    { name: "3 Jahre", value: "36" },
  ];

  get moreThanTwentyCounties(): boolean {
    return this.countyLinks.length > 20;
  }

  get otherCountiesInBundesland() {
    return this.countyLinks
      .filter(
        (county) =>
          decodePlaceName(county.link) !==
          decodePlaceName(this.routeCountyName),
      )
      .sort(() => 0.5 - Math.random())
      .slice(0, 20);
  }

  get countyYearPriceStatisctics() {
    return this.countyYearStatistics;
  }

  get showStateCityChart(): boolean {
    return (
      this.isDistrictCity &&
      ["Bremen", "Berlin", "Hamburg"].includes(
        translateStateName(replaceToUmlauts(this.$route.params.category)),
      )
    );
  }

  get historicalHighestDataCounty(): HistoricalOilPriceData[] {
    return getHistoricalData(this.countyYearStatistics, "highest");
  }

  get historicalLowestDataCounty(): HistoricalOilPriceData[] {
    return getHistoricalData(this.countyYearStatistics, "lowest");
  }

  get formattedTitle(): string {
    if (this.isDistrictCity) {
      return getFormattedTitle("in der kreisfreien Stadt", this.countyName);
    }
    return getFormattedTitle("im Landkreis", this.countyName);
  }

  get currentAverageCountyPrice(): string {
    return returnPriceFormatDE(this.stateAssetsPrice.currentPrice.value);
  }

  get currentPrice(): number {
    return this.stateAssetsPrice.currentPrice.value;
  }

  get lastPrice(): number {
    return this.stateAssetsPrice.lastPrice.value;
  }

  get lastPriceDifferenceText(): string {
    return getPriceDifferenceText(this.currentPrice, this.lastPrice);
  }

  get historicalHighestDataState(): HistoricalOilPriceData[] {
    return getHistoricalData(this.stateYearStatistics, "highest");
  }

  get historicalLowestDataState(): HistoricalOilPriceData[] {
    return getHistoricalData(this.stateYearStatistics, "lowest");
  }

  get historicalHighestData(): HistoricalOilPriceData[] {
    return this.showStateCityChart
      ? this.historicalHighestDataState
      : this.historicalHighestDataCounty;
  }

  get historicalLowestData(): HistoricalOilPriceData[] {
    return this.showStateCityChart
      ? this.historicalLowestDataState
      : this.historicalLowestDataCounty;
  }

  get showPriceComparisonHistoricalData(): boolean {
    return (
      (this.historicalHighestDataCounty && !this.showStateCityChart) ||
      (this.historicalHighestDataState && this.showStateCityChart)
    );
  }
}
